import React, { useCallback, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  Grid,
  Box,
  makeStyles,
  useTheme
} from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'

import Text from '../../atoms/Text'
import RoundedButton from '../../atoms/RoundedButton'
import Dialog from '../../molecules/Dialog/Dialog'
import TextInput from '../../molecules/TextInput'
import PasswordRequirements from '../../organisms/PasswordRequirements/PasswordRequirements'
import SnackAlert from '../../molecules/SnackAlert/SnackAlert'

import { useGetInputProps, useValidatePassword, useBoolean } from '../../../hooks'

import { changePassword } from '../../../service'

import { generatePassErrorMessage } from '../../../utils'
import { messageToUse } from '../../pages/admin/common'

import {
  BUTTON_SIZES,
  SUCCESS_ALERT,
  ERROR_ALERT,
  API_ERROR_CODES
} from '../../../constants'

const useStyles = makeStyles((theme) => ({
  resetPassHeader: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '0',
      paddingTop: '0',
      '& > h2 > p': {
        fontSize: '1.125rem !important'
      }
    }
  },
  resetPassBody: {
    padding: 0,
    marginTop: '3.25rem'
  },
  modalActions: {
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      '& > div': {
        flexDirection: 'column-reverse'
      }
    }
  },
  requirements: {
    display: 'flex',
    justifyContent: 'center',
    '& > div': {
      width: 'auto'
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      '& > div': {
        paddingLeft: '0'
      }
    }
  },
  confirmError: {
    textAlign: 'left',
    marginTop: '5px'
  }
}))

const validateConfirmPassword = (password, confirmPassword, errorKey, clearErrors) => {
  const isInvalid = (confirmPassword && password !== confirmPassword) || (password && confirmPassword !== password)

  if (isInvalid) {
    return 'Password does not match'
  } else {
    clearErrors(errorKey)
  }
}

const ResetPasswordDialog = ({ username, uid, openedDialog, closeDialog }) => {
  const classes = useStyles()
  const theme = useTheme()

  const [alert, setAlert] = useState({})
  const [isSaving, setIsSaving] = useBoolean(false)

  const [showPasswords, getInputProps] = useGetInputProps({
    oldPassowrd: false,
    password: false,
    confirmPassword: false
  }, false)

  const [passErrors, validatePassword] = useValidatePassword({
    stringLength: true,
    lowercase: true,
    uppercase: true,
    digit: true,
    confirmPassword: true,
    invalidPassword: true
  })

  const {
    handleSubmit,
    watch,
    control,
    register,
    clearErrors,
    reset,
    formState: { errors, isSubmitting, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: { oldPassword: '', password: '' }
  })

  const passwordRef = useRef()
  const confirmPassRef = useRef()
  passwordRef.current = watch('password', '')
  confirmPassRef.current = watch('confirmPassword', '')

  const oldPassword = watch('oldPassword')

  const onSubmitHandler = useCallback(async (values) => {
    try {
      setIsSaving.on()

      const updatePassBody = {
        oldPassword: {
          value: values.oldPassword
        },
        newPassword: {
          value: values.password
        }
      }

      await changePassword(uid, updatePassBody)

      closeDialog()
      reset()

      setAlert({ ...SUCCESS_ALERT })
    } catch (err) {
      if (err.code === API_ERROR_CODES.BAD_REQUEST) {
        ERROR_ALERT.alertMessage = generatePassErrorMessage(err)
      }
      setAlert({ ...ERROR_ALERT })
    } finally {
      setIsSaving.off()
    }
  }, [
    setIsSaving,
    closeDialog,
    uid,
    reset
  ])

  return (
    <>
      <Dialog size='md' open={openedDialog} onClose={closeDialog}>
        <form onSubmit={handleSubmit(onSubmitHandler)}>
          <Dialog.Header className={classes.resetPassHeader}>
            <Text
              text='Reset Password'
              color={theme.palette.summitBlue}
              customFontSize='1.5rem'
              customFontWeight='900'
            />
          </Dialog.Header>
          <Dialog.Body className={classes.resetPassBody}>
            <Grid container>
              <Grid item md={6} xs={12}>
                <Box marginBottom='1.125rem'>
                  <Controller
                    name='oldPassword'
                    render={() => (
                      <TextInput
                        type={showPasswords.oldPassword ? 'text' : 'password'}
                        inputProps={getInputProps('oldPassword')}
                        name='oldPassword'
                        placeholder='Old Password'
                        autoComplete='off'
                        fullWidth
                        {...register('oldPassword')}
                      />)}
                    control={control}
                    rules={{ required: true }}
                  />
                  {errors.oldPassword && (
                    <Text
                      text={messageToUse(errors.oldPassword)}
                      color={theme.palette.error.main}
                      className={classes.confirmError}
                    />
                  )}
                </Box>
                <Box marginBottom='1.125rem'>
                  <Controller
                    name='password'
                    render={() => (
                      <TextInput
                        type={showPasswords.password ? 'text' : 'password'}
                        inputProps={getInputProps('password')}
                        name='password'
                        placeholder='New password'
                        autocomplete='new-password'
                        fullWidth
                        {...register('password')}
                      />)}
                    control={control}
                    rules={oldPassword ? {
                      validate: {
                        password: (password) => {
                          const isInvalid = validatePassword(password, username)

                          if (isInvalid && errors.confirmPassword) {
                            clearErrors('confirmPassword')
                          }

                          if (isInvalid) return ''
                        },
                        confirmPassword: password => validateConfirmPassword(password, confirmPassRef.current, 'password', clearErrors)
                      }
                    } : {}}
                  />
                </Box>
                <Box>
                  <Controller
                    name='confirmPassword'
                    render={() => (
                      <TextInput
                        type={showPasswords.confirmPassword ? 'text' : 'password'}
                        inputProps={getInputProps('confirmPassword')}
                        name='confirmPassword'
                        placeholder='Confirm Password'
                        fullWidth
                        {...register('confirmPassword')}
                      />)}
                    control={control}
                    rules={oldPassword ? {
                      validate: {
                        confirmPassword: password => validateConfirmPassword(passwordRef.current, password, 'confirmPassword', clearErrors)
                      }
                    } : {}}
                  />
                  {(errors.confirmPassword || errors.password) && (
                    <Text
                      text={errors?.confirmPassword?.message ?? errors?.password?.message}
                      color={theme.palette.error.main}
                      className={classes.confirmError}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item md={6} xs={12} className={classes.requirements}>
                <PasswordRequirements errors={passErrors} noBackground />
              </Grid>
            </Grid>
          </Dialog.Body>
          <Dialog.Actions className={classes.modalActions}>
            <Grid container spacing={4} justifyContent='center'>
              <Grid item lg={4} xs={12}>
                <RoundedButton
                  fullWidth
                  onClick={closeDialog}
                  size={BUTTON_SIZES.large}
                >
                  Cancel
                </RoundedButton>
              </Grid>
              <Grid item lg={4} xs={12}>
                <RoundedButton
                  primary
                  fullWidth
                  type='submit'
                  size={BUTTON_SIZES.large}
                  isLoading={isSaving}
                  disabled={!isValid || isSubmitting}
                >
                  Save
                </RoundedButton>
              </Grid>
            </Grid>
          </Dialog.Actions>
        </form>
      </Dialog>
      <SnackAlert alert={alert} />
    </>
  )
}

ResetPasswordDialog.propTypes = {
  username: PropTypes.string.isRequired,
  openedDialog: PropTypes.bool.isRequired,
  uid: PropTypes.string.isRequired,
  closeDialog: PropTypes.func
}

export default ResetPasswordDialog
